import { deriveReelSections } from '@src/services/reel/deriveReelSections';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { AbstractCommand } from '../AbstractCommand';
import { InsertReelSectionsCommand } from './InsertReelSectionsCommand';
import { RemoveReelSectionCommand } from './RemoveReelSectionCommand';

import type { VideoEditor } from '../../VideoEditor';
import type { TimeRange } from '../lib/snapRangesToSceneBoundaries';

/**
 * PLAT-11071 — swaps a Section's passage for a different clip **in place**: same
 * position in the running order, same chapter, one undo entry.
 *
 * One clip in, however many passages that clip holds: an ordinary clip is one
 * range and an incoming *reel* is one per `subClips` entry, exactly as
 * `clipFragmentsFromClips` reads either (DE15). DS2's single-select is about how
 * many **clips** the swap takes, not about splitting one of them up — taking only
 * a reel's first passage would silently drop the rest of the clip the creator
 * picked.
 *
 * DS1 — a replace is an insert and a removal at one boundary, and both commands
 * already exist. This one owns the *pairing* and nothing else: it resolves the
 * target's index, runs the two, and delegates its undo back to them. Re-doing
 * the passage swap here would mean a third copy of the re-layout and
 * re-projection maths.
 *
 * **Insert first, then remove.** Two reasons, and the second is the one that
 * forces it:
 *
 * - Inserting at the target's index puts the new Section immediately *ahead* of
 *   it, so removing the old one leaves the replacement at exactly the index it
 *   vacated — the same result remove-then-insert gives.
 * - `RemoveReelSectionCommand` refuses to empty the reel (PLAT-11060 DS10). On a
 *   one-Section reel — which DE15 makes the common case — a remove-first pairing
 *   would be refused outright, and DS5 says replacing the only Section is
 *   allowed. Inserting first means there are always two Sections by the time the
 *   removal runs.
 *
 * The two sub-commands are run **directly** rather than through
 * `editor.executeCommand`, which is what makes the swap one undo entry rather
 * than two: `executeCommand` is patched by `ClipVideoCreatorAdapter` to push an
 * undo snapshot per command, and it also pushes each onto the editor's own
 * history — so `executeInBatch` (which only coalesces *notifications*) would not
 * have been enough.
 */
export class ReplaceReelSectionCommand extends AbstractCommand {
  private sectionId: string;
  private ranges: TimeRange[];
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private insert: InsertReelSectionsCommand | undefined;
  private remove: RemoveReelSectionCommand | undefined;

  /**
   * Provenance ids of the Sections that took the replaced one's place, in
   * playback order. One for an ordinary clip; one per passage for a clip that is
   * itself a reel, which is how `clipFragmentsFromClips` reads one (DE15).
   */
  readonly addedSectionIds: string[] = [];

  constructor(
    editor: VideoEditor,
    sectionId: string,
    ranges: TimeRange[],
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.sectionId = sectionId;
    this.ranges = ranges;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'ReplaceReelSectionCommand';
  }

  getDescription(): string {
    return `Replace reel section ${this.sectionId}`;
  }

  execute(): boolean {
    if (this.executed) return false;

    if (this.ranges.length === 0) {
      VideoEditorLogger.logCommand(this, 'error', { error: 'no_ranges' });
      return false;
    }

    const sections = deriveReelSections(this.editor.timeline);
    const index = sections.findIndex(s => s.id === this.sectionId);
    if (index === -1) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'section_not_found',
        sectionId: this.sectionId,
      });
      return false;
    }

    VideoEditorLogger.logCommand(this, 'start', {
      sectionId: this.sectionId,
      index,
    });

    const insert = new InsertReelSectionsCommand(
      this.editor,
      this.ranges,
      index,
      this.exemptElementIds,
    );
    if (!insert.execute()) {
      VideoEditorLogger.logCommand(this, 'error', { error: 'insert_failed' });
      return false;
    }

    const remove = new RemoveReelSectionCommand(
      this.editor,
      this.sectionId,
      this.exemptElementIds,
    );
    if (!remove.execute()) {
      // Nothing half-swapped is left behind: the reel goes back to the Sections
      // it had rather than keeping an arrival whose departure never happened.
      insert.undo();
      VideoEditorLogger.logCommand(this, 'error', { error: 'remove_failed' });
      return false;
    }

    this.insert = insert;
    this.remove = remove;
    this.addedSectionIds.push(...insert.insertedSectionIds);
    this.executed = true;

    VideoEditorLogger.logCommand(this, 'complete', {
      sectionId: this.sectionId,
      addedSectionIds: this.addedSectionIds,
      index,
    });
    return true;
  }

  undo(): boolean {
    if (!this.executed) return false;

    // Reverse order: the removal ran last, so it is taken back first — which
    // also means the insert's own snapshot is applied to the reel with the
    // replaced Section already back on it.
    const removeUndone = this.remove?.undo() ?? false;
    const insertUndone = this.insert?.undo() ?? false;

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result: removeUndone && insertUndone,
    });

    return removeUndone && insertUndone;
  }
}
